import { RolesEnum } from "../../tests/features/constants";

export const USER_ROLES_LIST = [
    {viewValue: 'Interpreting Physician', value: RolesEnum.Physician},
    {viewValue: 'Physician', value: RolesEnum.PhysicianNoSign},
    {viewValue: 'Scorer', value: RolesEnum.Scorer},
    {viewValue: 'Outsource Scorer', value: RolesEnum.OutsourceScorer},
    {viewValue: 'Technician', value: RolesEnum.Technician},
    {viewValue: 'Admin', value: RolesEnum.Admin},
    {viewValue: 'Viewer', value: RolesEnum.Viewer},
    {viewValue: 'Lab Manager', value: RolesEnum.LabManager},
    {viewValue: 'Tech Admin', value: RolesEnum.TechAdmin},
    {viewValue: 'External Interpreting Physician', value: RolesEnum.SharedPhysician},
  ];

/**
 * Tech Admin accounts belong to the platform's own staff rather than to the lab
 * using it, so every other role must be kept unaware that they exist. Super
 * Admin keeps full visibility — it is the root account that provisions them.
 */
export const canAccessTechAdmins = (role?: RolesEnum): boolean =>
  role === RolesEnum.TechAdmin || role === RolesEnum.SuperAdmin;

/**
 * The role options a viewer may pick from. The full list stays intact for label
 * lookups, so a hidden role still renders correctly for the viewers allowed to
 * see it; only the offered choices shrink.
 */
export const visibleUserRoles = (role?: RolesEnum) =>
  canAccessTechAdmins(role)
    ? USER_ROLES_LIST
    : USER_ROLES_LIST.filter((r) => r.value !== RolesEnum.TechAdmin);

/** A role as offered by the server: `canAssign` says whether it can be set. */
export interface UserRoleOption {
  value: RolesEnum;
  viewValue: string;
  canAssign: boolean;
}

  export const ROLE_LABEL_MAP =  {
  [RolesEnum.SuperAdmin]: 'Super Admin',
  [RolesEnum.Physician]: 'Interpreting Physician',
  [RolesEnum.PhysicianNoSign]: 'Physician',
  [RolesEnum.Scorer]: 'Scorer',
  [RolesEnum.OutsourceScorer]: 'Outsource Scorer',
  [RolesEnum.Technician]: 'Technician',
  [RolesEnum.Admin]: 'Admin',
  [RolesEnum.Viewer]: 'Viewer',
  [RolesEnum.LabManager]: 'Lab Manager',
  [RolesEnum.TechAdmin]: 'Tech Admin',
  [RolesEnum.SharedPhysician]: 'External Interpreting Physician',
  ['PATIENT']: 'Patient',
};


export const DEFAULT_PAGES: Record<RolesEnum, string> = {
  [RolesEnum.Physician]: "/dashboard",
  [RolesEnum.PhysicianNoSign]: "/dashboard",
  [RolesEnum.Scorer]:"/dashboard",
  [RolesEnum.Technician]: "/dashboard",
  [RolesEnum.LabManager]: "/dashboard",
  [RolesEnum.Admin]: "/dashboard",
  [RolesEnum.Viewer]:"/dashboard",
  [RolesEnum.SuperAdmin]:"/dashboard",
  [RolesEnum.OutsourceScorer]:"/tests",
  [RolesEnum.TechAdmin]:"/dashboard",
  [RolesEnum.SharedPhysician]:"/physician-dashboard",
}

// Role management permissions: defines which roles can be managed by each role (positive approach)
export const ROLE_MANAGEMENT_PERMISSIONS: Record<RolesEnum, RolesEnum[]> = {
  [RolesEnum.SuperAdmin]: [RolesEnum.TechAdmin, RolesEnum.Admin, RolesEnum.Physician, RolesEnum.PhysicianNoSign, RolesEnum.Scorer, RolesEnum.OutsourceScorer, RolesEnum.Technician, RolesEnum.Viewer, RolesEnum.LabManager, RolesEnum.SharedPhysician],
  [RolesEnum.TechAdmin]: [RolesEnum.TechAdmin, RolesEnum.Admin, RolesEnum.Physician, RolesEnum.PhysicianNoSign, RolesEnum.Scorer, RolesEnum.OutsourceScorer, RolesEnum.Technician, RolesEnum.Viewer, RolesEnum.LabManager, RolesEnum.SharedPhysician],
  [RolesEnum.Admin]: [RolesEnum.Physician, RolesEnum.PhysicianNoSign, RolesEnum.Scorer, RolesEnum.OutsourceScorer, RolesEnum.Technician, RolesEnum.Viewer, RolesEnum.LabManager, RolesEnum.SharedPhysician],
  [RolesEnum.Physician]: [],
  [RolesEnum.PhysicianNoSign]: [],
  [RolesEnum.Scorer]: [],
  [RolesEnum.Technician]: [],
  [RolesEnum.Viewer]: [],
  [RolesEnum.LabManager]: [],
  [RolesEnum.OutsourceScorer]: [],
  [RolesEnum.SharedPhysician]: [],
};

/**
 * Stand-in for the roles endpoint, used only when that request fails. The
 * dropdowns keep working from the bundled matrix, which may lag the server's —
 * which is the whole reason the server owns it.
 */
export const localRoleOptions = (role?: RolesEnum): UserRoleOption[] => {
  const assignable = role ? ROLE_MANAGEMENT_PERMISSIONS[role] || [] : [];
  return visibleUserRoles(role).map((option) => ({
    ...option,
    canAssign: assignable.includes(option.value),
  }));
};

export const SUPERVISOR_EMAILS = [
  'einat@dormotech.com',
  'saar@dormotech.com',
  'ofir@dormotech.com',
  'noa@dormotech.com',
  'chen@dormotech.com',
];
